import { Fragment, useContext } from "react";
import { isNil } from "lodash";
import { useLocation } from "react-router-dom";
import { getObjectDisplayForAttached } from "common-client/utils/customObject";
import { ActivityTargetType, ActivityType } from "../../generated/graphql";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { AuthContext } from "../Authorization/AuthContext";
import { Body } from "../Common/Typography";
import { useEditTaskModal } from "../Tasks/Form/EditTaskForm";
import { StyledAnchor, StyledLink } from "./__styles__/Message";
import { groupMessagePartBuilder } from "./MessagePartBuilders/groupMessagePartBuilder";
import {
  generateInfoPanelLink,
  isTaskTarget,
  MessagePartBuilders,
} from "./MessagePartBuilders/messagePartBuilder";
import { MessageProps } from "./MessagePartBuilders/types";
import { Activity } from "./types";

const isTargetDeleted = (activity: Activity) => {
  const targetIsDeletable = [
    ActivityTargetType.CERTIFICATE,
    ActivityTargetType.DOCUMENT_UPLOAD,
    ActivityTargetType.LOG,
    ActivityTargetType.SUBMISSION,
    ActivityTargetType.TASK,
    ActivityTargetType.WORKFLOW,
  ].includes(activity.targetType);

  return targetIsDeletable && isNil(activity.target);
};

const Message = ({
  creator,
  feedSource,
  activity,
  linkToObject,
}: MessageProps) => {
  const { user, admin, account } = useContext(AuthContext);

  // Set up task modal for task activities
  const [showTaskModal] = useEditTaskModal({
    taskId:
      isTaskTarget(activity.target) ?
        activity.target.id
        // This *shouldn't* ever happen; we ensure that the activity target is a
        // task before calling showTaskModal; however, the task modal requires
        // specifying the taskId at hook instantiation time rather than at call
        // time, so *something* needs to be here. If this does end up as the
        // taskId, however, the EditTaskForm handles the error case and will
        // show the appropriate message.
      : "not-a-real-task-id",
  });

  const trackClick = () => {
    track("Clicked link in activity feed", {
      userId: user?.id ?? admin?.id,
      userEmail: user?.email ?? admin?.email,
      source: feedSource,
    });
  };

  const location = useLocation();

  const messagePartBuilderArgs = {
    activity,
    targetId: activity.targetInfo.id ?? undefined,
    accountId: activity.accountId,
    subdomain: account!.subdomain,
    feedSource,
    showTaskModal,
  };

  const messagePartBuilder =
    activity.isGroup ?
      groupMessagePartBuilder
    : MessagePartBuilders[activity.targetType as ActivityTargetType];
  const messageResult = messagePartBuilder(messagePartBuilderArgs);

  const { action, indefiniteArticle, targetTextParts } = messageResult;
  const targetDeleted = isTargetDeleted(activity);

  const TargetText = (
    <>
      {targetTextParts.map(({ linkProps, text, href, onClick }, index) => {
        const key = `${activity.id}-${index}`;
        if (targetDeleted) {
          return <Fragment key={key}>{text}</Fragment>;
        }

        if (!!linkProps) {
          return (
            <StyledLink
              key={key}
              to={{
                ...linkProps,
                state: { prevLocation: getCurrentURL(location) },
              }}
              onClick={trackClick}
            >
              {text}
            </StyledLink>
          );
        }

        if (!!href) {
          return (
            <StyledAnchor
              href={href}
              onClick={trackClick}
              target="_blank"
              key={key}
            >
              {text}
            </StyledAnchor>
          );
        }

        if (!!onClick) {
          return (
            <StyledAnchor
              key={key}
              onClick={e => {
                e.preventDefault();
                trackClick();
                onClick();
              }}
              style={{ cursor: "pointer" }}
            >
              {text}
            </StyledAnchor>
          );
        }

        return <Fragment key={key}>{text}</Fragment>;
      })}
    </>
  );

  const objectLabel = getObjectDisplayForAttached(activity);

  const objectLink = objectLabel && linkToObject && (
    <>
      {(
        [
          ActivityType.EXCLUDED,
          ActivityType.INCLUDED,
          ActivityType.HID,
          ActivityType.DISPLAYED,
        ].includes(activity.type)
      ) ?
        "at"
      : "for"}{" "}
      <StyledLink
        to={{
          state: { prevLocation: getCurrentURL(location) },
          ...generateInfoPanelLink({ activity }),
        }}
        onClick={trackClick}
      >
        {objectLabel}
      </StyledLink>
    </>
  );

  const openingClause = `${creator} ${
    action ?? activity.type
  } ${indefiniteArticle} `;

  return (
    <Body size="default" type="regular" color="contentPrimary">
      {openingClause} {TargetText} {objectLink}
    </Body>
  );
};

export default Message;
